import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { FormField } from "@colab/shared";
import type { AdminFormRow } from "./types";

export type AddFieldInput = {
  formId: string;
  sectionId: string;
  field: FormField;
};

type Context = {
  previousDetail: AdminFormRow | undefined;
};

// POST /api/admin/forms/:formId/fields
//
// Optimistic — appends the field to the target section in the detail cache,
// rolls back on error. The client supplies the field id (UUID) so the
// optimistic id matches the server's, avoiding a temp-row swap on success.
//
// Layered on top of the whole-definition PATCH server-side; this hook is the
// editor's drag-add / "Add field" button surface.
export function useAddField() {
  const queryClient = useQueryClient();
  return useMutation<{ id: string }, ApiClientError, AddFieldInput, Context>({
    mutationFn: ({ formId, sectionId, field }) =>
      apiFetch<{ id: string }>(`/admin/forms/${formId}/fields`, {
        method: "POST",
        body: JSON.stringify({ sectionId, field }),
      }),
    onMutate: async ({ formId, sectionId, field }) => {
      const detailKey = queryKeys.adminForms.detail(formId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<AdminFormRow>(detailKey);
      queryClient.setQueryData<AdminFormRow>(detailKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          definition: {
            ...old.definition,
            sections: old.definition.sections.map((s) =>
              s.id === sectionId ? { ...s, fields: [...s.fields, field] } : s,
            ),
          },
        };
      });
      return { previousDetail };
    },
    onError: (err, { formId }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.adminForms.detail(formId),
          context.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to add field");
    },
    onSettled: (_data, _err, { formId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminForms.detail(formId),
      });
    },
  });
}
